import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useShopifyMerchant } from "@/hooks/useShopifyMerchant";
import { useSubscription } from "@/hooks/useSubscription";
import { useTeamMemberProfile } from "@/hooks/useTeamMemberProfile";
import { useNavigationBlocker, setBypassBlocker } from "@/hooks/useNavigationBlocker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  Clock, 
  CheckCircle, 
  Loader2,
  Search,
  ExternalLink,
  Wallet,
  Package,
  Store,
  LogOut,
  Bell,
  Receipt,
  Gift,
  RotateCcw,
  Banknote,
  Settings,
  Menu,
  Briefcase,
  Activity,
  Plus,
  FileText,
  Truck,
  MapPin,
  ChevronDown,
  ChevronRight,
  Mail,
  Shield,
  ArrowLeft
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usesWalletBillingForMerchant } from "@/lib/billing";
import { cn } from "@/lib/utils";
import { formatCurrency, type Currency } from "@/lib/currency";
import {
  clearShopifyAutoReconnectAttempt,
  maybeStartShopifyAutoReconnect,
} from "@/lib/shopifyAutoReconnect";
import { getShopifyEmbeddedContext } from "@/lib/shopifyEmbedded";
import { getPreferredMerchantAssociation, normalizeShopDomain } from "@/lib/merchantSelection";
import { completePendingMerchantSetup } from "@/lib/merchantSetup";
import { fetchSupabaseFunction } from "@/lib/shopifySessionToken";
import { toast } from "sonner";
import { buildPublicAppUrl } from "@/lib/appConfig";
import { GST_MODULE_ENABLED } from "@/lib/featureFlags";
import MerchantSettings from "@/components/MerchantSettings";
import MerchantPreferences from "@/components/MerchantPreferences";
import { TrialBanner } from "@/components/TrialBanner";
import { SubscriptionPayment } from "@/components/SubscriptionPayment";
import { PaymentHistory } from "@/components/PaymentHistory";
import { MerchantWalletPanel } from "@/components/MerchantWalletPanel";
import { MerchantConnectorPanel } from "@/components/MerchantConnectorPanel";
import { FinancialStatement } from "@/components/FinancialStatement";
import { MerchantAnalytics } from "@/components/MerchantAnalytics";
// ProductShakatahManager removed - split payments are now store-wide
import { CustomerInsights } from "@/components/CustomerInsights";
import { FAQChatBot } from "@/components/FAQChatBot";
import { DashboardOverview } from "@/components/DashboardOverview";
import { TeamMemberSetup } from "@/components/TeamMemberSetup";
import { DashboardTour } from "@/components/DashboardTour";
import { ContactSupport } from "@/components/ContactSupport";
import { ReviewRequestDialog } from "@/components/ReviewRequestDialog";
import { MerchantSidebar, PlatformTheme } from "@/components/MerchantSidebar";
import { NotificationSettings } from "@/components/NotificationSettings";
import { ShopifyShippingDetails } from "@/components/ShopifyShippingDetails";
import { ExitConfirmationDialog } from "@/components/ExitConfirmationDialog";

import { CustomerFooter } from "@/components/CustomerFooter";
import RefundManagement from "@/components/RefundManagement";
import { CreditCard, UserCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  clearMerchantSupportSession,
  getMerchantSupportSession,
} from "@/lib/merchantSupportSession";

interface Participant {
  id: string;
  name: string;
  email: string;
  mobile: string;
  has_paid: boolean;
  is_organizer?: boolean | null;
  midpoint_reminder_sent: boolean;
  final_reminder_sent: boolean;
  immediate_reminder_sent?: boolean | null;
  shopify_order_id?: string | null;
}

interface SplitPaymentRequest {
  id: string;
  product_name: string;
  product_image_url: string | null;
  product_description: string | null;
  total_price: number;
  split_amount: number;
  deadline: string;
  deadline_type: string;
  customer_name: string;
  customer_email: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  shopify_order_id?: string | null;
  tax_rate: number;
  inventory_reserved: boolean;
  fulfillment_status: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  carrier: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  participants: Participant[];
}

interface GiftVoucher {
  id: string;
  voucher_number: string;
  amount: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  status: string;
  issued_at: string;
  redeemed_at: string | null;
  participant_name?: string;
  participant_email?: string;
  product_name?: string;
}

interface RedemptionInvoice {
  id: string;
  invoice_number: string;
  product_name: string;
  product_amount: number;
  total_vouchers_redeemed: number;
  total_voucher_amount: number;
  redeemed_at: string;
}

interface SplitInvoice {
  id: string;
  request_id: string;
  invoice_number: string;
  invoice_type: string;
  participant_email: string;
  participant_name: string | null;
  product_title: string;
  subtotal: number;
  tax_amount: number | null;
  total_amount: number;
  status: string;
  generated_at: string;
  sent_at: string | null;
  currency: string;
  pdf_url: string | null;
  shopify_order_id: string | null;
  shopify_order_name: string | null;
}

interface ShopifyOrderSummary {
  id: string;
  legacyResourceId: string | null;
  orderName: string;
  createdAt: string;
  financialStatus: string;
  fulfillmentStatus: string;
  cancelledAt: string | null;
  totalAmount: number;
  currencyCode: string;
  customerName: string;
  customerEmail: string | null;
  statusPageUrl: string | null;
  adminOrderUrl: string | null;
}

interface CancellationFee {
  id: string;
  request_id: string;
  fee_amount: number;
  merchant_share: number;
  shakatah_share: number;
  status: string;
  created_at: string;
  invoiced_at: string | null;
  paid_at: string | null;
  product_name?: string;
  customer_name?: string;
}


interface ShakatahInvoice {
  id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total_fees: number;
  total_amount: number;
  status: string;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
}

interface MerchantInfo {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  shopify_access_token?: string | null;
  notification_email: string | null;
  plan: string;
  business_address?: string | null;
  gst_number?: string | null;
  business_email?: string | null;
  business_phone?: string | null;
  billing_provider?: string | null;
  contact_person_name?: string | null;
  currency?: string;
  widget_version?: number | null;
  widget_synced_at?: string | null;
  platform_type?: string;
  signup_source?: string | null;
  onboarding_completed_at?: string | null;
  webhook_secret?: string | null;
  widget_installed?: boolean | null;
}

interface InvoiceFunctionResult {
  success?: boolean;
  invoices_generated?: number;
  invoices?: Array<{
    invoice_id: string;
    invoice_number: string;
    participant_email: string;
    amount: number;
    type: string;
  }>;
  message?: string;
  error?: string;
  pdf_base64?: string | null;
  pdf_file_name?: string | null;
}

const GST_INVOICE_PAGE_SIZE = 8;

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { role: merchantRole, isSuperAdmin, isManager, isViewOnly, canManageRefunds, canManageProducts, canAccessSettings, merchantId: roleMerchantId, loading: roleLoading } = useShopifyMerchant();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { needsSetup, needsTour, refetch: refetchProfile } = useTeamMemberProfile(roleMerchantId);
  const [requests, setRequests] = useState<SplitPaymentRequest[]>([]);
  
  const [vouchers, setVouchers] = useState<GiftVoucher[]>([]);
  const [splitInvoices, setSplitInvoices] = useState<SplitInvoice[]>([]);
  const [shopifyOrders, setShopifyOrders] = useState<ShopifyOrderSummary[]>([]);
  const [redemptionInvoices, setRedemptionInvoices] = useState<RedemptionInvoice[]>([]);
  const [cancellationFees, setCancellationFees] = useState<CancellationFee[]>([]);
  const [shakatahInvoices, setShakatahInvoices] = useState<ShakatahInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "completed" | "expired">("all");
  const [voucherFilter, setVoucherFilter] = useState<"all" | "issued" | "redeemed" | "pending_refund" | "refunded">("all");
  const [merchantInfo, setMerchantInfo] = useState<MerchantInfo | null>(null);
  const storeCurrency = ((merchantInfo?.currency || "INR").toUpperCase() as Currency);
  const [markingRefund, setMarkingRefund] = useState<string | null>(null);
  const [bulkRefundProcessing, setBulkRefundProcessing] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showCreateCollectionDialog, setShowCreateCollectionDialog] = useState(false);
  const [paidOrdersPage, setPaidOrdersPage] = useState(1);
  const [generatedInvoicesPage, setGeneratedInvoicesPage] = useState(1);
  const [expandedOrderGroups, setExpandedOrderGroups] = useState<Record<string, boolean>>({});
  const [supportSession, setSupportSession] = useState(getMerchantSupportSession);

  const billingSubscription = useMemo(() => {
    if (subscription) {
      return subscription;
    }

    if (!merchantInfo) {
      return null;
    }

    const introTrialSplitLimit = 100;
    const trialEndsAt = (merchantInfo as any).trial_ends_at ? new Date((merchantInfo as any).trial_ends_at) : null;
    const trialStartedAt = (merchantInfo as any).trial_started_at ? new Date((merchantInfo as any).trial_started_at) : null;
    const billingCycleStart = (merchantInfo as any).billing_cycle_start ? new Date((merchantInfo as any).billing_cycle_start) : null;
    const now = new Date();

    let daysRemaining: number | null = null;
    let isTrialExpired = false;

    if (trialEndsAt) {
      const diff = trialEndsAt.getTime() - now.getTime();
      daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
      isTrialExpired = daysRemaining < 0;
    }

    const effectivePlan =
      merchantInfo.plan === "trial" && isTrialExpired
        ? "trial_expired"
        : merchantInfo.plan;

    return {
      plan: effectivePlan,
      status: (merchantInfo as any).subscription_status || "trialing",
      trialEndsAt,
      trialStartedAt,
      billingCycleStart,
      splitsThisMonth: 0,
      daysRemaining: daysRemaining !== null && daysRemaining > 0 ? daysRemaining : null,
      isTrialExpired,
      isActive:
        (merchantInfo as any).subscription_status === "active" ||
        ((merchantInfo as any).subscription_status === "trialing" && !isTrialExpired),
      canCreateSplit: true,
      splitLimit: effectivePlan === "trial" ? introTrialSplitLimit : null,
      freeSplitAllowance: effectivePlan === "trial" ? introTrialSplitLimit : effectivePlan === "free" ? 5 : effectivePlan === "starter" ? 25 : effectivePlan === "growth" ? 100 : null,
      overageFeePercent: effectivePlan === "free" ? 2 : effectivePlan === "starter" ? 1.5 : effectivePlan === "growth" ? 1 : 0,
    };
  }, [merchantInfo, subscription]);

  const billingPlanLabel = useMemo(() => {
    if (!billingSubscription) {
      return null;
    }

    if (billingSubscription.status === "canceled" || billingSubscription.status === "past_due" || billingSubscription.status === "paused") {
      return "No active plan";
    }

    if (billingSubscription.plan === "trial_expired") {
      return "Trial expired";
    }

    return billingSubscription.plan.charAt(0).toUpperCase() + billingSubscription.plan.slice(1);
  }, [billingSubscription]);

  const usesWalletBilling = useMemo(() => {
    if (subscription?.billingProvider === "razorpay") {
      return true;
    }

    if (merchantInfo?.platform_type === "shopify" && getShopifyEmbeddedContext().embedded) {
      return false;
    }

    return usesWalletBillingForMerchant(merchantInfo as any);
  }, [merchantInfo?.billing_provider, merchantInfo?.platform_type, merchantInfo?.signup_source, subscription?.billingProvider]);

  // Navigation blocker to confirm before leaving dashboard
  const { 
    showDialog: showExitDialog, 
    confirmNavigation, 
    cancelNavigation 
  } = useNavigationBlocker({ when: true });

  // Get navigation state from URL params for proper browser history support
  const activeTab = searchParams.get("tab") || "overview";
  const ordersSubView = (searchParams.get("ordersView") as "requests" | "vouchers" | "invoices" | "shipping" | "refunds") || "requests";
  const insightsSubView = (searchParams.get("insightsView") as "analytics" | "customers") || "analytics";
  const settingsSubView = (searchParams.get("settingsView") as "billing" | "connector" | "preferences" | "shipping" | "notifications" | "info") || "billing";
  
  // Note: Professional sub-views removed - professional dashboard is now a separate page

  // Navigation functions that update URL (enables browser back button support)
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", tab);
      // Reset sub-views when changing main tab
      newParams.delete("ordersView");
      newParams.delete("insightsView");
      newParams.delete("settingsView");
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setOrdersSubView = useCallback((view: "requests" | "vouchers" | "invoices" | "shipping" | "refunds") => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("ordersView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setInsightsSubView = useCallback((view: "analytics" | "customers") => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("insightsView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setSettingsSubView = useCallback((view: "billing" | "connector" | "preferences" | "shipping" | "notifications" | "info") => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("settingsView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  // Helper to set both tab and subview when navigating to billing
  // MUST be defined before any early returns to avoid React hooks order violation
  const handleNavigateToBilling = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", "settings");
      newParams.set("settingsView", "billing");
      newParams.delete("ordersView");
      newParams.delete("insightsView");
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  // Check if team member needs setup (non-super_admin only)
  useEffect(() => {
    if (!roleLoading && !isSuperAdmin && needsSetup) {
      setShowSetup(true);
    } else if (!roleLoading && !isSuperAdmin && needsTour) {
      setShowTour(true);
    }
  }, [roleLoading, isSuperAdmin, needsSetup, needsTour]);

  useEffect(() => {
    if (!merchantInfo || loading) {
      return;
    }

    if (merchantInfo.platform_type !== "shopify") {
      return;
    }

    if (searchParams.get("oauth_success") || searchParams.get("oauth_error")) {
      return;
    }

    const embeddedShop = searchParams.get("shop");

    if (merchantInfo.shopify_access_token) {
      clearShopifyAutoReconnectAttempt(merchantInfo.id, embeddedShop);
      return;
    }

    const startedReconnect = maybeStartShopifyAutoReconnect({
      hasAccessToken: merchantInfo.shopify_access_token,
      merchantId: merchantInfo.id,
      merchantShopDomain: merchantInfo.shopify_store_domain,
      searchParams,
    });

    if (startedReconnect) {
      toast.info("Refreshing Shopify access for your store...");
    }
  }, [loading, merchantInfo, searchParams]);
  

  const fetchMerchantInfo = async () => {
    if (!user) return null;

    try {
      const storedSupportSession = getMerchantSupportSession();
      if (searchParams.get("support") === "1" && !storedSupportSession) {
        toast.error("Your merchant support session has expired");
        setBypassBlocker(true);
        navigate("/admin", { replace: true });
        setTimeout(() => setBypassBlocker(false), 100);
        return null;
      }

      if (storedSupportSession) {
        const { data: activeSupportSession, error: supportError } = await (supabase as any)
          .rpc("get_active_merchant_support_session");

        if (
          !supportError &&
          activeSupportSession?.session_id === storedSupportSession.sessionId &&
          activeSupportSession?.merchant_id === storedSupportSession.merchantId
        ) {
          const { data: supportMerchant, error: supportMerchantError } = await supabase
            .from("merchants")
            .select("*")
            .eq("id", storedSupportSession.merchantId)
            .single();

          if (!supportMerchantError && supportMerchant) {
            localStorage.setItem("active_merchant_id", storedSupportSession.merchantId);
            setSupportSession(storedSupportSession);
            setMerchantInfo(supportMerchant as unknown as MerchantInfo);
            return storedSupportSession.merchantId;
          }
        }

        clearMerchantSupportSession();
        setSupportSession(null);
        toast.error("Your merchant support session has expired");
        setBypassBlocker(true);
        navigate("/admin", { replace: true });
        setTimeout(() => setBypassBlocker(false), 100);
        return null;
      }

      // Get the user's merchant associations (may have multiple: shopify + professional)
      const { data: merchantUsers, error: merchantUserError } = await supabase
        .from('merchant_users')
        .select('merchant_id, role, merchants!inner(platform_type, shopify_store_domain)')
        .eq('user_id', user.id);

      if (merchantUserError || !merchantUsers || merchantUsers.length === 0) {
        const repairedMerchantId = await completePendingMerchantSetup(user);
        if (repairedMerchantId) {
          localStorage.setItem('active_merchant_id', repairedMerchantId);
          toast.success("Store setup completed successfully.");
          setBypassBlocker(true);
          navigate('/merchant/onboarding', { replace: true });
          setTimeout(() => setBypassBlocker(false), 100);
          return null;
        }

        console.error("No merchant association found:", merchantUserError);
        toast.error("No store linked to your account. Please contact support.");
        return null;
      }

      const embeddedShop = normalizeShopDomain(searchParams.get("shop"));
      const storedMerchantId = localStorage.getItem('active_merchant_id');
      const merchantUser = getPreferredMerchantAssociation(merchantUsers as any[], {
        preferredShop: embeddedShop,
        storedMerchantId,
      });

      if (!merchantUser) {
        return null;
      }

      localStorage.setItem('active_merchant_id', merchantUser.merchant_id);

      // Get merchant details
      const { data: merchant, error: merchantError } = await supabase
        .from('merchants')
        .select('*')
        .eq('id', merchantUser.merchant_id)
        .single();

      if (merchantError || !merchant) {
        console.error("Failed to fetch merchant:", merchantError);
        return null;
      }

      setMerchantInfo(merchant as unknown as MerchantInfo);
      return merchantUser.merchant_id;
    } catch (error) {
      console.error("Error fetching merchant info:", error);
      return null;
    }
  };

  const fetchRequests = async (merchantId: string) => {
    try {
      // Fetch split payment requests for this merchant only
      const { data: requestsData, error: requestsError } = await supabase
        .from('split_payment_requests')
        .select('*')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
      } else {
        const requestIds = requestsData.map(r => r.id);
        const { data: participantsData, error: participantsError } = await supabase
          .from('split_payment_participants')
          .select('*')
          .in('request_id', requestIds);

        if (participantsError) throw participantsError;

        const combinedRequests = requestsData.map(request => ({
          ...request,
          participants: (participantsData || []).filter(p => p.request_id === request.id)
        }));

        setRequests(combinedRequests);
      }

      // Fetch gift vouchers for this merchant's requests
      const { data: vouchersData, error: vouchersError } = await supabase
        .from('gift_vouchers')
        .select('*, split_payment_participants(name, email), split_payment_requests!inner(product_name, merchant_id)')
        .eq('split_payment_requests.merchant_id', merchantId)
        .order('issued_at', { ascending: false });

      if (!vouchersError && vouchersData) {
        const formattedVouchers = vouchersData.map(v => ({
          ...v,
          participant_name: v.split_payment_participants?.name,
          participant_email: v.split_payment_participants?.email,
          product_name: v.split_payment_requests?.product_name,
        }));
        setVouchers(formattedVouchers);
      }

      // Fetch split payment invoices for this merchant
      if (GST_MODULE_ENABLED) {
        const { data: splitInvoicesData, error: splitInvoicesError } = await supabase
          .from('split_payment_invoices')
          .select('*')
          .eq('merchant_id', merchantId)
          .eq('invoice_type', 'final_gst')
          .order('generated_at', { ascending: false });

        if (!splitInvoicesError && splitInvoicesData) {
          setSplitInvoices(splitInvoicesData as unknown as SplitInvoice[]);
        }
      } else {
        setSplitInvoices([]);
      }

      const { data: merchantSummary } = await supabase
        .from("merchants")
        .select("platform_type, shopify_access_token")
        .eq("id", merchantId)
        .maybeSingle();

      if (merchantSummary?.platform_type === "shopify" && merchantSummary?.shopify_access_token) {
        try {
          const ordersResponse = await fetchSupabaseFunction("list-shopify-orders", {
            body: { merchant_id: merchantId, limit: 250 },
          });
          const ordersResult = await ordersResponse.json();

          if (!ordersResponse.ok) {
            throw new Error(ordersResult.error || "Failed to fetch Shopify orders");
          }

          setShopifyOrders(ordersResult.orders || []);
        } catch (ordersError) {
          console.error("Error fetching Shopify orders:", ordersError);
          setShopifyOrders([]);
        }
      } else {
        setShopifyOrders([]);
      }

      // Fetch redemption invoices for this merchant's requests
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('redemption_invoices')
        .select('*, split_payment_requests!inner(merchant_id)')
        .eq('split_payment_requests.merchant_id', merchantId)
        .order('redeemed_at', { ascending: false });

      if (!invoicesError && invoicesData) {
        setRedemptionInvoices(invoicesData);
      }

      // Fetch cancellation fees for this merchant
      const { data: feesData, error: feesError } = await supabase
        .from('cancellation_fees')
        .select('*, split_payment_requests(product_name, customer_name)')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (!feesError && feesData) {
        const formattedFees = feesData.map(f => ({
          ...f,
          product_name: f.split_payment_requests?.product_name,
          customer_name: f.split_payment_requests?.customer_name,
        }));
        setCancellationFees(formattedFees);
      }

      // Fetch Shakatah invoices for this merchant
      const { data: shakatahInvoicesData, error: shakatahInvoicesError } = await supabase
        .from('shakatah_invoices')
        .select('*')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (!shakatahInvoicesError && shakatahInvoicesData) {
        setShakatahInvoices(shakatahInvoicesData);
      }


    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const [noMerchantAssociation, setNoMerchantAssociation] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      const merchantId = await fetchMerchantInfo();
      if (merchantId) {
        await fetchRequests(merchantId);
      } else {
        // No merchant found - user signed up but merchant record wasn't created
        setNoMerchantAssociation(true);
        setLoading(false);
      }
    };
    loadData();
  }, [searchParams, user]);

  useEffect(() => {
    if (!GST_MODULE_ENABLED && ordersSubView === "invoices") {
      setOrdersSubView("requests");
    }
  }, [ordersSubView, setOrdersSubView]);

  // Redirect to onboarding if not completed or no merchant association - hard block
  // But only redirect if the onboarding page can handle the case (has account repair logic)
  useEffect(() => {
    if (loading) return;
    
    // Handle case where user has no merchant association at all
    // Check if there's pending Shopify data - if so, redirect to merchant onboarding
    // Otherwise, default to professional onboarding for account repair
    if (noMerchantAssociation) {
      toast.info("Please complete your account setup to continue");
      // Bypass navigation blocker for redirect to onboarding
      setBypassBlocker(true);
      
      // Default to merchant onboarding — professional onboarding is only for
      // users who explicitly chose the professional/standalone path.
      // Session storage for Shopify may have been cleared, so don't rely on it.
      navigate('/merchant/onboarding', { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
      return;
    }
    
    if (!merchantInfo) return;
    
    // If onboarding is not completed, redirect to appropriate onboarding
    if (!merchantInfo.onboarding_completed_at) {
      const domain = merchantInfo.shopify_store_domain || '';
      const platformType = merchantInfo.platform_type;
      
      const onboardingPath = '/merchant/onboarding';
      
      toast.info("Please complete your setup to continue");
      // Bypass navigation blocker for redirect to onboarding
      setBypassBlocker(true);
      navigate(onboardingPath, { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
    }
  }, [loading, merchantInfo, noMerchantAssociation, navigate]);

  // Only run once when role loading completes, not on every settingsSubView change
  useEffect(() => {
    if (!roleLoading && !canAccessSettings && (settingsSubView === 'billing' || settingsSubView === 'connector')) {
      // Only redirect from admin-only default settings views
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set("settingsView", "notifications");
        return newParams;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLoading, canAccessSettings]);

  const handleSignOut = async () => {
    // Bypass the navigation blocker for sign-out
    const { setBypassBlocker } = await import("@/hooks/useNavigationBlocker");
    setBypassBlocker(true);

    if (supportSession) {
      await (supabase as any).rpc("end_merchant_support_session", {
        _session_id: supportSession.sessionId,
      });
      clearMerchantSupportSession();
      setSupportSession(null);
    }
    
    await signOut();
    // Use replace to remove dashboard from history, preventing forward navigation back
    navigate("/merchant-login", { replace: true });
    toast.success("Signed out successfully");
    
    // Reset bypass flag (cleanup)
    setBypassBlocker(false);
  };

  const handleEndSupportSession = async () => {
    if (supportSession) {
      const { error } = await (supabase as any).rpc("end_merchant_support_session", {
        _session_id: supportSession.sessionId,
      });
      if (error) {
        console.error("Failed to end merchant support session:", error);
      }
    }

    clearMerchantSupportSession();
    localStorage.removeItem("active_merchant_id");
    setSupportSession(null);
    setBypassBlocker(true);
    navigate("/admin", { replace: true });
    setTimeout(() => setBypassBlocker(false), 100);
  };

  useEffect(() => {
    if (!supportSession) {
      return;
    }

    const remainingMs = new Date(supportSession.expiresAt).getTime() - Date.now();
    if (remainingMs <= 0) {
      clearMerchantSupportSession();
      setSupportSession(null);
      setBypassBlocker(true);
      navigate("/admin", { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
      return;
    }

    const expiryTimer = window.setTimeout(() => {
      clearMerchantSupportSession();
      localStorage.removeItem("active_merchant_id");
      setSupportSession(null);
      toast.info("Merchant support session expired");
      setBypassBlocker(true);
      navigate("/admin", { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
    }, remainingMs);

    return () => window.clearTimeout(expiryTimer);
  }, [navigate, supportSession]);

  const markRefundComplete = async (voucherId: string) => {
    try {
      setMarkingRefund(voucherId);
      const { error } = await supabase
        .from('gift_vouchers')
        .update({ status: 'refunded' })
        .eq('id', voucherId);

      if (error) throw error;

      // Send notification email to participant
      try {
        await fetchSupabaseFunction("notify-refund-complete", {
          body: { voucher_id: voucherId },
        });
      } catch (emailError) {
        console.error("Failed to send refund notification email:", emailError);
        // Don't fail the whole operation if email fails
      }

      setVouchers(prev => prev.map(v => 
        v.id === voucherId ? { ...v, status: 'refunded' } : v
      ));

      toast.success('Refund marked as complete - participant notified');
    } catch (error) {
      console.error("Error marking refund complete:", error);
      toast.error("Failed to update refund status");
    } finally {
      setMarkingRefund(null);
    }
  };

  const markAllRefundsComplete = async () => {
    const pendingRefundVouchers = vouchers.filter(v => v.status === 'pending_refund');
    if (pendingRefundVouchers.length === 0) {
      toast.info('No pending refunds to process');
      return;
    }

    setBulkRefundProcessing(true);
    let successCount = 0;
    let failCount = 0;

    for (const voucher of pendingRefundVouchers) {
      try {
        const { error } = await supabase
          .from('gift_vouchers')
          .update({ status: 'refunded' })
          .eq('id', voucher.id);

        if (error) throw error;

        // Send notification email
        try {
          await fetchSupabaseFunction("notify-refund-complete", {
            body: { voucher_id: voucher.id },
          });
        } catch (emailError) {
          console.error("Failed to send refund notification email:", emailError);
        }

        successCount++;
      } catch (error) {
        console.error("Error marking refund complete:", error);
        failCount++;
      }
    }

    // Update local state
    setVouchers(prev => prev.map(v => 
      v.status === 'pending_refund' ? { ...v, status: 'refunded' } : v
    ));

    setBulkRefundProcessing(false);

    if (failCount === 0) {
      toast.success(`All ${successCount} refunds marked as complete - participants notified`);
    } else {
      toast.warning(`${successCount} refunds completed, ${failCount} failed`);
    }
  };

  // Generate GST invoices for fully paid split requests
  const [generatingInvoice, setGeneratingInvoice] = useState<string | null>(null);
  const [bulkGeneratingInvoices, setBulkGeneratingInvoices] = useState(false);
  const [sendingOrderEmailId, setSendingOrderEmailId] = useState<string | null>(null);

  const downloadBase64Pdf = useCallback((base64: string, fileName: string) => {
    const byteCharacters = window.atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
  }, []);

  const requestGstInvoicePdf = useCallback(async (
    requestId: string,
    sendEmail = false,
  ): Promise<InvoiceFunctionResult> => {
    const response = await fetchSupabaseFunction("generate-split-invoice", {
      body: { request_id: requestId, send_email: sendEmail },
    });
    const result: InvoiceFunctionResult = await response.json();

    if (!response.ok || result.error) {
      throw new Error(result.error || "Failed to generate GST invoice");
    }

    return result;
  }, []);

  const buildSplitOrderUrl = useCallback((requestId: string) => (
    buildPublicAppUrl(`/split/${requestId}`)
  ), []);

  const openSplitOrderTracking = useCallback((requestId: string) => {
    window.open(buildSplitOrderUrl(requestId), "_blank", "noopener,noreferrer");
  }, [buildSplitOrderUrl]);

  const sendManualOrderEmail = useCallback(async (request: SplitPaymentRequest) => {
    const actualStatus = getRequestStatus(request.status, request.deadline);

    try {
      setSendingOrderEmailId(request.id);

      if (actualStatus === "pending") {
        const response = await fetchSupabaseFunction("send-participant-checkouts", {
          body: { requestId: request.id, force: true },
        });
        const result = await response.json();

        if (!response.ok || result.error) {
          throw new Error(result.error || "Failed to send participant invite emails");
        }

        const sentCount = Number(result.sent || 0);
        toast.success(
          sentCount > 0
            ? `Sent ${sentCount} invite email${sentCount === 1 ? "" : "s"}`
            : "No unpaid participant invite emails were pending",
        );
        return;
      }

      const response = await fetchSupabaseFunction("send-order-status-email", {
        body: {
          request_id: request.id,
          merchant_id: merchantInfo?.id,
        },
      });
      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || "Failed to send order update email");
      }

      const sentCount = Number(result.sent_count || 0);
      toast.success(
        sentCount > 0
          ? `Sent order update email to ${sentCount} recipient${sentCount === 1 ? "" : "s"}`
          : "Order email was not sent because no recipients were found",
      );
    } catch (error: any) {
      console.error("Error sending manual order email:", error);
      toast.error(error.message || "Failed to send order email");
    } finally {
      setSendingOrderEmailId(null);
    }
  }, [merchantInfo?.id]);

  const generateInvoicesForRequest = async (request: SplitPaymentRequest) => {
    try {
      setGeneratingInvoice(request.id);
      const existingInvoice = splitInvoices.find((invoice) => invoice.request_id === request.id);

      if (existingInvoice) {
        const existingResult = await requestGstInvoicePdf(request.id, false);
        if (!existingResult.pdf_base64 || !existingResult.pdf_file_name) {
          throw new Error("GST invoice PDF is not available yet");
        }
        downloadBase64Pdf(existingResult.pdf_base64, existingResult.pdf_file_name);
        toast.success("GST invoice downloaded");
        return;
      }

      const result = await requestGstInvoicePdf(request.id, true);
      
      if (result.invoices_generated === 0) {
        if (result.pdf_base64 && result.pdf_file_name) {
          downloadBase64Pdf(result.pdf_base64, result.pdf_file_name);
          toast.success("GST invoice downloaded");
        } else {
          toast.info(result.message || "GST invoice is only available after 100% payment is collected");
        }
      } else {
        if (merchantInfo?.id) {
          await fetchRequests(merchantInfo.id);
        }

        if (!result.pdf_base64 || !result.pdf_file_name) {
          throw new Error("GST invoice PDF could not be prepared");
        }

        downloadBase64Pdf(result.pdf_base64, result.pdf_file_name);
        toast.success(`${result.invoices_generated} GST invoice generated, emailed, and downloaded`);
      }
    } catch (error: any) {
      console.error("Invoice generation error:", error);
      toast.error(`Failed to generate GST invoice: ${error.message}`);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const generateAllPendingInvoices = async () => {
    const eligibleRequests = requests.filter(r => {
      const { paid, total } = getPaidCount(r.participants);
      return total > 0 && paid === total && r.status === 'completed';
    });

    if (eligibleRequests.length === 0) {
      toast.info("No fully paid split orders are waiting for a GST invoice");
      return;
    }

    setBulkGeneratingInvoices(true);
    let totalGenerated = 0;

    for (const request of eligibleRequests) {
      try {
        const response = await fetchSupabaseFunction("generate-split-invoice", {
          body: { request_id: request.id, send_email: true },
        });
        const result = await response.json();
        if (result.invoices_generated > 0) {
          totalGenerated += result.invoices_generated;
        }
      } catch (error) {
        console.error(`Failed to generate invoices for ${request.id}:`, error);
      }
    }

    setBulkGeneratingInvoices(false);
    if (totalGenerated > 0) {
      toast.success(`${totalGenerated} GST invoice(s) generated across completed orders`);
    } else {
      toast.info("All completed split orders already have a GST invoice");
    }
  };

  const downloadExistingInvoiceFromPortal = async (invoice: SplitInvoice) => {
    try {
      setGeneratingInvoice(invoice.request_id);
      const result = await requestGstInvoicePdf(invoice.request_id, false);

      if (!result.pdf_base64 || !result.pdf_file_name) {
        throw new Error("GST invoice PDF is not available yet");
      }

      downloadBase64Pdf(result.pdf_base64, result.pdf_file_name);
      toast.success("GST invoice downloaded");
    } catch (error: any) {
      console.error("GST invoice download error:", error);
      toast.error(error.message || "Failed to download GST invoice");
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const renderSimplePagination = (
    page: number,
    totalPages: number,
    onPageChange: (page: number) => void,
  ) => {
    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className="flex items-center justify-between gap-3 border rounded-lg px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {page} of {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const getRequestStatus = (status: string, deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const isExpired = now > deadlineDate;

    if (status === 'completed') return 'completed';
    if (status === 'expired' || isExpired) return 'expired';
    return 'pending';
  };

  const getStatusBadge = (status: string, deadline: string) => {
    const actualStatus = getRequestStatus(status, deadline);

    if (actualStatus === 'completed') {
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Completed</Badge>;
    }
    if (actualStatus === 'expired') {
      return <Badge variant="destructive">Expired</Badge>;
    }
    return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Pending</Badge>;
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();

    if (diff <= 0) return "Expired";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h left`;
    }
    return `${hours}h ${minutes}m left`;
  };

  const getPaidCount = (participants: Participant[]) => {
    const paid = participants.filter(p => p.has_paid).length;
    return { paid, total: participants.length };
  };


  // Filter requests
  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.participants.some(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const actualStatus = getRequestStatus(request.status, request.deadline);
    const matchesStatus = statusFilter === "all" || actualStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const matchesSharedSearch = useCallback((values: Array<string | null | undefined>) => {
    if (!normalizedSearchQuery) {
      return true;
    }

    return values.some((value) => value?.toLowerCase().includes(normalizedSearchQuery));
  }, [normalizedSearchQuery]);

  const buildPlatformOrderUrl = useCallback((orderId?: string | null) => {
    if (!orderId || !merchantInfo?.shopify_store_domain) {
      return null;
    }

    if (merchantInfo.platform_type === "woocommerce") {
      const wooOrderId = String(orderId).replace(/^woo-/, "").trim();
      if (!wooOrderId) {
        return null;
      }

      return `${merchantInfo.shopify_store_domain.replace(/\/+$/, "")}/wp-admin/post.php?post=${encodeURIComponent(wooOrderId)}&action=edit`;
    }

    const normalizedOrderId = String(orderId).replace(/^shopify-/, "").trim();
    const storeHandle = merchantInfo.shopify_store_domain.replace(".myshopify.com", "");
    return `https://admin.shopify.com/store/${storeHandle}/orders/${normalizedOrderId}`;
  }, [merchantInfo?.platform_type, merchantInfo?.shopify_store_domain]);

  const orderReferenceLabel = merchantInfo?.platform_type === "woocommerce" ? "Main WooCommerce Ref" : "Main Shopify Ref";
  const orderButtonLabel = merchantInfo?.platform_type === "woocommerce" ? "Woo Order" : "Shopify Order";

  const paidSplitRequests = useMemo(() => (
    requests.filter((request) => {
      const { paid, total } = getPaidCount(request.participants);
      return total > 0 && paid === total && request.status === "completed";
    })
  ), [requests]);

  const splitRequestsByShopifyOrderId = useMemo(() => {
    const requestMap = new Map<string, SplitPaymentRequest>();

    requests.forEach((request) => {
      if (request.shopify_order_id) {
        requestMap.set(String(request.shopify_order_id), request);
      }
    });

    return requestMap;
  }, [requests]);

  const splitInvoicesByShopifyOrderId = useMemo(() => {
    const invoiceMap = new Map<string, SplitInvoice>();

    splitInvoices.forEach((invoice) => {
      if (invoice.shopify_order_id) {
        invoiceMap.set(String(invoice.shopify_order_id), invoice);
      }
    });

    return invoiceMap;
  }, [splitInvoices]);

  const paidShopifyOrders = useMemo(() => (
    shopifyOrders.filter((order) => order.financialStatus === "PAID")
  ), [shopifyOrders]);

  const filteredPaidShopifyOrders = useMemo(() => (
    paidShopifyOrders.filter((order) => matchesSharedSearch([
      order.orderName,
      order.customerName,
      order.customerEmail,
      order.legacyResourceId,
    ]))
  ), [matchesSharedSearch, paidShopifyOrders]);

  const filteredSplitInvoices = useMemo(() => (
    splitInvoices.filter((invoice) => matchesSharedSearch([
      invoice.invoice_number,
      invoice.participant_name,
      invoice.participant_email,
      invoice.product_title,
      invoice.shopify_order_id,
      invoice.shopify_order_name,
    ]))
  ), [matchesSharedSearch, splitInvoices]);

  const totalPaidOrdersPages = Math.max(1, Math.ceil(filteredPaidShopifyOrders.length / GST_INVOICE_PAGE_SIZE));
  const totalGeneratedInvoicesPages = Math.max(1, Math.ceil(filteredSplitInvoices.length / GST_INVOICE_PAGE_SIZE));
  const paginatedPaidShopifyOrders = filteredPaidShopifyOrders.slice(
    (paidOrdersPage - 1) * GST_INVOICE_PAGE_SIZE,
    paidOrdersPage * GST_INVOICE_PAGE_SIZE,
  );
  const paginatedSplitInvoices = filteredSplitInvoices.slice(
    (generatedInvoicesPage - 1) * GST_INVOICE_PAGE_SIZE,
    generatedInvoicesPage * GST_INVOICE_PAGE_SIZE,
  );

  useEffect(() => {
    setPaidOrdersPage(1);
  }, [normalizedSearchQuery, shopifyOrders, merchantInfo?.id]);

  useEffect(() => {
    setGeneratedInvoicesPage(1);
  }, [normalizedSearchQuery, splitInvoices, merchantInfo?.id]);

  useEffect(() => {
    if (paidOrdersPage > totalPaidOrdersPages) {
      setPaidOrdersPage(totalPaidOrdersPages);
    }
  }, [paidOrdersPage, totalPaidOrdersPages]);

  useEffect(() => {
    if (generatedInvoicesPage > totalGeneratedInvoicesPages) {
      setGeneratedInvoicesPage(totalGeneratedInvoicesPages);
    }
  }, [generatedInvoicesPage, totalGeneratedInvoicesPages]);


  if (loading || roleLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show team member setup for non-super_admin
  if (showSetup && merchantInfo && user && merchantRole) {
    return (
      <TeamMemberSetup
        merchantId={merchantInfo.id}
        userId={user.id}
        role={merchantRole}
        storeName={merchantInfo.store_name}
        onComplete={() => {
          setShowSetup(false);
          refetchProfile();
          setShowTour(true);
        }}
      />
    );
  }



  // Determine platform theme based on platform_type and domain
  const getPlatformTheme = (): PlatformTheme => {
    const platformType = merchantInfo?.platform_type;
    const domain = merchantInfo?.shopify_store_domain || '';
    
    if (platformType === 'shopify') {
      return 'shopify';
    }
    if (platformType === 'woocommerce') {
      return 'woocommerce';
    }
    return 'standalone'; // Default for direct/standalone stores
  };
  
  const platformTheme = getPlatformTheme();

  // Helper to get theme classes for header
  const getHeaderThemeClasses = () => {
    switch (platformTheme) {
      case 'shopify':
        return { bg: 'bg-shopify/10', text: 'text-shopify', border: 'border-shopify/50' };
      case 'woocommerce':
        return { bg: 'bg-woocommerce/10', text: 'text-woocommerce', border: 'border-woocommerce/50' };
      default:
        return { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/50' };
    }
  };
  
  const headerTheme = getHeaderThemeClasses();

  // Get platform label for badge
  const getPlatformLabel = () => {
    switch (platformTheme) {
      case 'shopify': return 'Shopify';
      case 'woocommerce': return 'WooCommerce';
      default: return 'Direct Store';
    }
  };

   // Merchant-only system (professional vertical removed)
  
  const sidebarContent = (
    <MerchantSidebar
      activeTab={activeTab}
      ordersSubView={ordersSubView}
      insightsSubView={insightsSubView}
      settingsSubView={settingsSubView}
      onTabChange={(tab, subView) => {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set("tab", tab);
          newParams.delete("ordersView");
          newParams.delete("insightsView");
          newParams.delete("settingsView");
          if (subView) {
            if (tab === "orders") newParams.set("ordersView", subView);
            else if (tab === "insights") newParams.set("insightsView", subView);
            else if (tab === "settings") newParams.set("settingsView", subView);
          }
          return newParams;
        }, { replace: false });
        setMobileSidebarOpen(false);
      }}
      onOrdersSubViewChange={setOrdersSubView}
      onInsightsSubViewChange={setInsightsSubView}
      onSettingsSubViewChange={setSettingsSubView}
      pendingOrdersCount={requests.filter(r => r.status === 'pending').length}
      pendingRefundsCount={vouchers.filter(v => v.status === 'pending_refund' || v.status === 'refund_failed').length}
      pendingFeesCount={cancellationFees.filter(f => f.status === 'pending').length}
      canManageProducts={canManageProducts}
      canAccessSettings={canAccessSettings}
      platformTheme={platformTheme}
      showWalletMenu={usesWalletBilling}
    />
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {supportSession && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-amber-950">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4" />
              <strong>Support mode:</strong>
              <span>
                Viewing {supportSession.merchantName}. Access expires at{" "}
                {new Date(supportSession.expiresAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}.
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={handleEndSupportSession}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Return to Admin
            </Button>
          </div>
        </div>
      )}
      {/* Dashboard Tour for team members */}
      {showTour && user && (isManager || isViewOnly) && (
        <DashboardTour
          userId={user.id}
          role={isManager ? 'manager' : 'view_only'}
          run={showTour}
          onComplete={() => setShowTour(false)}
        />
      )}
      
      {/* Review Request Dialog - shows after 100 splits or 1 month of paid subscription */}
      {merchantInfo && subscription && isSuperAdmin && (
        <ReviewRequestDialog
          merchantId={merchantInfo.id}
          merchantName={merchantInfo.store_name}
          totalSplits={requests.length}
          subscriptionStartDate={subscription.plan !== 'trial' ? (merchantInfo as any).billing_cycle_start : null}
          plan={subscription.plan}
        />
      )}
      
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-20">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile menu trigger */}
              <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-64">
                  {sidebarContent}
                </SheetContent>
              </Sheet>
              
              <div className={cn("p-2 rounded-lg hidden sm:block", headerTheme.bg)}>
                <Store className={cn("h-5 w-5", headerTheme.text)} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold text-sm sm:text-base">{merchantInfo?.contact_person_name || merchantInfo?.store_name || 'Dashboard'}</h1>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-xs hidden sm:inline-flex",
                      headerTheme.border,
                      headerTheme.text,
                      headerTheme.bg
                    )}
                  >
                    {getPlatformLabel()}
                  </Badge>
                  {merchantRole && (
                    <Badge variant="outline" className="capitalize text-xs hidden sm:inline-flex">
                      {merchantRole.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  {user?.email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <ContactSupport 
                merchantName={merchantInfo?.store_name}
                merchantEmail={merchantInfo?.notification_email || undefined}
              />
              {merchantInfo?.shopify_store_domain && platformTheme === 'shopify' && (
                <Button 
                  size="sm" 
                  className="hidden sm:flex"
                  onClick={() => window.open(`https://${merchantInfo.shopify_store_domain}/admin`, '_blank')}
                >
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.337 3.415c-.193-.016-.4.013-.544.064-.144.05-.514.193-.667.257-.153.065-.41.193-.514.224-.103.032-.205.046-.256.064-.052.016-.116.032-.167.032h-.064c-.116-.064-.244-.145-.373-.193-.129-.048-.27-.08-.41-.096-.142-.016-.283-.016-.373.016-.09.032-.18.08-.27.144-.09.065-.18.145-.257.225-.077.08-.142.16-.206.24-.064.081-.116.161-.167.225-.052.064-.09.112-.116.144l-.09.129c-.038.048-.077.112-.128.192-.052.08-.116.176-.18.304-.064.129-.129.273-.193.449-.065.177-.116.385-.168.625-.05.241-.077.514-.077.803 0 .289.026.593.077.882.052.288.129.56.206.802.077.24.154.449.244.624.09.177.18.32.257.433.077.112.142.192.193.256.052.065.09.097.116.113l.052.048c.026.016.052.048.09.08.039.032.09.08.155.129.064.048.128.096.206.144.077.048.167.096.257.145.09.048.193.08.296.112.103.032.206.048.296.048.09 0 .167-.016.232-.032.064-.016.116-.032.154-.064.039-.032.065-.064.078-.096.012-.032.012-.064.012-.08v-.032c0-.016-.012-.032-.012-.048 0-.016-.013-.032-.026-.064-.012-.032-.038-.064-.064-.112-.026-.048-.064-.08-.103-.129-.039-.048-.077-.08-.116-.112-.039-.032-.077-.064-.116-.08-.038-.016-.077-.032-.103-.048-.025-.016-.051-.032-.064-.032-.012-.016-.025-.016-.038-.016h-.026c-.013 0-.026 0-.052.016-.025.016-.064.032-.115.064-.052.032-.116.064-.18.112-.065.048-.142.096-.219.16-.078.065-.155.129-.232.193-.078.065-.155.129-.22.177-.064.048-.115.08-.154.097-.038.016-.064.016-.077.016h-.026z"/>
                  </svg>
                  MyStore
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSignOut}
                className="gap-1 sm:gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block h-[calc(100vh-57px)] sticky top-[57px]">
          {sidebarContent}
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto pb-12">
          <div className="p-4 sm:p-6 max-w-5xl">
            {/* Trial/Subscription Banner */}
            {subscription && (
              <TrialBanner
                plan={subscription.plan}
                status={subscription.status}
                daysRemaining={subscription.daysRemaining}
                isTrialExpired={subscription.isTrialExpired}
                splitsThisMonth={subscription.splitsThisMonth}
                splitLimit={subscription.splitLimit}
                onUpgradeClick={handleNavigateToBilling}
                onExtendTrialClick={() => {
                  const subject = encodeURIComponent(`Trial Extension Request - ${merchantInfo?.store_name || 'My Business'}`);
                  const body = encodeURIComponent(`Hi Shakatah Team,\n\nI would like to request an extension for my trial.\n\nStore: ${merchantInfo?.store_name || 'N/A'}\nSplits used: ${subscription.splitsThisMonth}${subscription.splitLimit ? `/${subscription.splitLimit}` : ''}\n\nThank you!`);
                  window.open(`mailto:hello@shakatah.com?subject=${subject}&body=${body}`, '_blank');
                }}
              />
            )}

            {/* Content Sections */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                <DashboardOverview
                  requests={requests}
                  merchantInfo={merchantInfo}
                  subscription={subscription}
                  onNavigateToTab={(tab, subView) => {
                    setSearchParams(prev => {
                      const newParams = new URLSearchParams(prev);
                      newParams.set("tab", tab);
                      newParams.delete("ordersView");
                      newParams.delete("insightsView");
                      newParams.delete("settingsView");
                      if (tab === "settings" && subView) {
                        newParams.set("settingsView", subView);
                      }
                      return newParams;
                    }, { replace: false });
                  }}
                />
              </div>
            )}


            {activeTab === "insights" && (
              <div className="space-y-6">
              <MerchantAnalytics 
                requests={requests}
                splitsThisMonth={subscription?.splitsThisMonth || 0}
                splitLimit={subscription?.splitLimit || null}
                currency={storeCurrency}
                onNavigateToOrders={(filter) => {
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set("tab", "orders");
                    newParams.set("ordersView", "history");
                    if (filter) newParams.set("statusFilter", filter);
                    return newParams;
                  }, { replace: false });
                }}
              />
              </div>
            )}

            {activeTab === "orders" && (
              <div className="space-y-6">
                {/* Requests Sub-View */}
                {ordersSubView === "requests" && (
                  <div className="space-y-6">
                    {/* Order Status Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <Package className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Total Orders</p>
                              <p className="text-2xl font-bold">{requests.length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-500/10 rounded-lg">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Fulfilled</p>
                              <p className="text-2xl font-bold text-green-600">{requests.filter(r => getRequestStatus(r.status, r.deadline) === 'completed').length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-yellow-500/10 rounded-lg">
                              <Clock className="h-4 w-4 text-yellow-600" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Awaiting Payments</p>
                              <p className="text-2xl font-bold text-yellow-600">{requests.filter(r => getRequestStatus(r.status, r.deadline) === 'pending').length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-500/10 rounded-lg">
                              <Users className="h-4 w-4 text-red-600" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Expired</p>
                              <p className="text-2xl font-bold text-red-600">{requests.filter(r => getRequestStatus(r.status, r.deadline) === 'expired').length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Gift Voucher Invoicing Info */}
                    <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex gap-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg h-fit">
                            <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">How Split Payment Invoicing Works</p>
                            <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
                              Each participant's share is invoiced as a <strong>Gift Voucher</strong> — not a product sale. 
                              These vouchers are automatically generated when a participant pays. 
                              The actual <strong>product invoice</strong> should only be issued once all payments are collected and the order is fulfilled. 
                              This dual-invoice model ensures correct GST/tax treatment: voucher issuance is tax-exempt, while the final product sale attracts applicable taxes.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Search and Filters */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by product, organizer, or participant..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <div className="flex gap-2">
                        {(["all", "pending", "completed", "expired"] as const).map((status) => (
                          <Button
                            key={status}
                            variant={statusFilter === status ? "default" : "outline"}
                            size="sm"
                            onClick={() => setStatusFilter(status)}
                            className="capitalize"
                          >
                            {status}
                          </Button>
                        ))}
                      </div>
                    </div>

                {/* Orders List */}
                {filteredRequests.length === 0 && requests.length > 0 ? (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-lg font-semibold mb-2">No matching orders</h2>
                    <p className="text-muted-foreground">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-20">
                    <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">No split payment orders yet</h2>
                    <p className="text-muted-foreground mb-6">
                      Split payment orders will appear here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredRequests.map((request) => {
                      const { paid, total } = getPaidCount(request.participants);
                      const allPaid = paid === total;
                      const pendingContributionAmount = Math.max(
                        Number(request.total_price) - (paid * Number(request.split_amount)),
                        0,
                      );
                      const shareOrderParticipants = request.participants
                        .filter((participant) => participant.shopify_order_id && participant.shopify_order_id !== request.shopify_order_id)
                        .sort((a, b) => Number(Boolean(b.is_organizer)) - Number(Boolean(a.is_organizer)));
                      const hasOrderGroup = Boolean(request.shopify_order_id) || shareOrderParticipants.length > 0;
                      const isOrderGroupExpanded = Boolean(expandedOrderGroups[request.id]);

                      return (
                        <Card key={request.id} className="overflow-hidden">
                          <div className="flex">
                            {request.product_image_url && (
                              <div className="w-32 h-32 flex-shrink-0 bg-muted">
                                <img 
                                  src={request.product_image_url} 
                                  alt={request.product_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h3 className="font-semibold">{request.product_name}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    by {request.customer_name} • {new Date(request.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {getStatusBadge(request.status, request.deadline)}
                                  {hasOrderGroup && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-8 gap-1.5"
                                      onClick={() => setExpandedOrderGroups((current) => ({
                                        ...current,
                                        [request.id]: !current[request.id],
                                      }))}
                                    >
                                      {isOrderGroupExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                      Orders
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Total: </span>
                                  <span className="font-medium">{formatCurrency(request.total_price, storeCurrency)}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Per Person: </span>
                                  <span className="font-medium text-primary">{formatCurrency(request.split_amount, storeCurrency)}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Progress: </span>
                                  <span className={`font-medium ${allPaid ? 'text-green-600' : ''}`}>
                                    {paid}/{total} paid
                                  </span>
                                </div>
                                {request.status === 'pending' && (
                                  <div>
                                    <span className="text-muted-foreground">Pending amount: </span>
                                    <span className="font-medium text-amber-600">{formatCurrency(pendingContributionAmount, storeCurrency)}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">{getTimeRemaining(request.deadline)}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">{orderReferenceLabel}: </span>
                                  <span className="font-medium">
                                    {request.shopify_order_id ? `#${request.shopify_order_id}` : "Pending sync"}
                                  </span>
                                </div>
                                {shareOrderParticipants.length > 0 && (
                                  <Badge variant="outline" className="text-xs border-purple-300 text-purple-600 bg-purple-50 dark:bg-purple-950 dark:border-purple-700 dark:text-purple-300">
                                    {shareOrderParticipants.length} non-shippable share order{shareOrderParticipants.length === 1 ? "" : "s"}
                                  </Badge>
                                )}
                                {request.status === 'pending' && request.inventory_reserved && (
                                  <Badge variant="outline" className="text-xs border-blue-300 text-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-400">
                                    🔒 Inventory Reserved
                                  </Badge>
                                )}
                              </div>
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => openSplitOrderTracking(request.id)}
                                  className="gap-1.5"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  Track Order
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => sendManualOrderEmail(request)}
                                  disabled={sendingOrderEmailId === request.id}
                                  className="gap-1.5"
                                >
                                  {sendingOrderEmailId === request.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Mail className="h-3.5 w-3.5" />
                                  )}
                                  Send Email
                                </Button>
                                {request.shopify_order_id && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const orderUrl = buildPlatformOrderUrl(request.shopify_order_id);
                                      if (orderUrl) {
                                        window.open(orderUrl, '_blank');
                                      }
                                    }}
                                    className="gap-1.5"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    {orderButtonLabel}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          {hasOrderGroup && isOrderGroupExpanded && (
                            <div className="border-t bg-muted/20 px-4 py-3 space-y-3">
                              <div className="flex items-center justify-between gap-3 rounded-lg border bg-background p-3">
                                <div className="flex items-center gap-3">
                                  <div className="rounded-md bg-green-500/10 p-2">
                                    <Package className="h-4 w-4 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">Main shippable order</p>
                                    <p className="text-xs text-muted-foreground">
                                      Final product order created after full split payment
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {request.shopify_order_id ? (
                                    <>
                                      <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                                        #{request.shopify_order_id}
                                      </Badge>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          const orderUrl = buildPlatformOrderUrl(request.shopify_order_id);
                                          if (orderUrl) {
                                            window.open(orderUrl, '_blank');
                                          }
                                        }}
                                        className="gap-1.5"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                        Open
                                      </Button>
                                    </>
                                  ) : (
                                    <Badge variant="outline">Pending final payment</Badge>
                                  )}
                                </div>
                              </div>
                              {shareOrderParticipants.map((participant) => (
                                <div key={participant.id} className="flex items-center justify-between gap-3 rounded-lg border bg-background p-3">
                                  <div className="flex items-center gap-3">
                                    <div className="rounded-md bg-purple-500/10 p-2">
                                      <Receipt className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm">
                                        {participant.is_organizer ? "Organizer share order" : "Participant share order"}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {participant.name} • {participant.email}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="border-purple-300 text-purple-600 bg-purple-50 dark:bg-purple-950 dark:border-purple-700 dark:text-purple-300">
                                      Non-shippable
                                    </Badge>
                                    <Badge className={participant.has_paid ? "bg-green-500/10 text-green-600 border-green-500/20" : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"}>
                                      #{participant.shopify_order_id}
                                    </Badge>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const orderUrl = buildPlatformOrderUrl(participant.shopify_order_id);
                                        if (orderUrl) {
                                          window.open(orderUrl, '_blank');
                                        }
                                      }}
                                      className="gap-1.5"
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" />
                                      Open
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Vouchers Sub-View */}
            {ordersSubView === "vouchers" && (
              <div className="space-y-6">
            {/* Voucher Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Gift className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Vouchers</p>
                      <p className="text-2xl font-bold">{vouchers.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/10 rounded-lg">
                      <Receipt className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Issued</p>
                      <p className="text-2xl font-bold text-yellow-600">{vouchers.filter(v => v.status === 'issued').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Redeemed</p>
                      <p className="text-2xl font-bold text-green-600">{vouchers.filter(v => v.status === 'redeemed').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <Wallet className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Value</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(vouchers.reduce((sum, v) => sum + Number(v.total_amount), 0), storeCurrency)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filter and Bulk Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex gap-2 flex-wrap">
                {(["all", "issued", "redeemed", "pending_refund", "refunded"] as const).map((status) => (
                  <Button
                    key={status}
                    variant={voucherFilter === status ? "default" : "outline"}
                    size="sm"
                    onClick={() => setVoucherFilter(status)}
                    className="capitalize"
                  >
                    {status === 'pending_refund' ? 'Pending Refund' : status}
                  </Button>
                ))}
              </div>
              
              {canManageRefunds && vouchers.filter(v => v.status === 'pending_refund').length > 0 && (
                <Button
                  onClick={markAllRefundsComplete}
                  disabled={bulkRefundProcessing}
                >
                  {bulkRefundProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark All Refunds Complete ({vouchers.filter(v => v.status === 'pending_refund').length})
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Vouchers List */}
            <div className="space-y-3">
              {vouchers.filter(v => voucherFilter === "all" || v.status === voucherFilter).map((voucher) => (
                <Card key={voucher.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          voucher.status === 'redeemed' ? 'bg-green-500/10' : 
                          voucher.status === 'pending_refund' ? 'bg-orange-500/10' :
                          voucher.status === 'refunded' ? 'bg-blue-500/10' :
                          'bg-yellow-500/10'
                        }`}>
                          {voucher.status === 'pending_refund' || voucher.status === 'refunded' ? (
                            <RotateCcw className={`h-5 w-5 ${
                              voucher.status === 'refunded' ? 'text-blue-600' : 'text-orange-600'
                            }`} />
                          ) : (
                            <Gift className={`h-5 w-5 ${voucher.status === 'redeemed' ? 'text-green-600' : 'text-yellow-600'}`} />
                          )}
                        </div>
                        <div>
                          <p className="font-mono font-semibold text-primary">{voucher.voucher_number}</p>
                          <p className="text-sm text-muted-foreground">{voucher.participant_name} • {voucher.product_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(voucher.total_amount, storeCurrency)}</p>
                          <Badge className={
                            voucher.status === 'redeemed' ? 'bg-green-500/10 text-green-600' : 
                            voucher.status === 'pending_refund' ? 'bg-orange-500/10 text-orange-600' :
                            voucher.status === 'refunded' ? 'bg-blue-500/10 text-blue-600' :
                            'bg-yellow-500/10 text-yellow-600'
                          }>
                            {voucher.status === 'pending_refund' ? 'Pending Refund' : voucher.status}
                          </Badge>
                        </div>
                        {canManageRefunds && voucher.status === 'pending_refund' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markRefundComplete(voucher.id)}
                            disabled={markingRefund === voucher.id}
                            className="border-green-500/30 text-green-600 hover:bg-green-500/10"
                          >
                            {markingRefund === voucher.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            Mark Refund Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {vouchers.length === 0 && (
                <div className="text-center py-12">
                  <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No gift vouchers yet</p>
                </div>
              )}
            </div>

            {/* Redemption Invoices */}
            {redemptionInvoices.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Redemption Invoices</CardTitle>
                  <CardDescription>Completed split payment redemptions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {redemptionInvoices.map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <p className="font-mono font-semibold text-sm">{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">{invoice.product_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">{formatCurrency(invoice.total_voucher_amount, storeCurrency)}</p>
                          <p className="text-xs text-muted-foreground">{invoice.total_vouchers_redeemed} vouchers</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              )}
              </div>
            )}

            {/* Invoices Sub-View */}
            {GST_MODULE_ENABLED && ordersSubView === "invoices" && (
              <div className="space-y-6">
                {/* GST Invoice Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Paid Shopify Orders</p>
                          <p className="text-2xl font-bold">{paidShopifyOrders.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Ready for GST PDF</p>
                          <p className="text-2xl font-bold text-green-600">{paidSplitRequests.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-500/10 rounded-lg">
                          <Clock className="h-4 w-4 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Generated GST PDFs</p>
                          <p className="text-2xl font-bold text-yellow-600">{splitInvoices.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <Wallet className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Emailed GST PDFs</p>
                          <p className="text-lg font-bold text-green-600">{splitInvoices.filter((invoice) => Boolean(invoice.sent_at)).length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg h-fit">
                        <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">GST Invoices & Paid Shopify Orders</p>
                        <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
                          This menu shows paid Shopify orders for reference, plus the GST PDFs that SplitKart has generated for fully collected split orders.
                          Every row includes the Shopify order reference so your team can reconcile the portal, email attachments, and Shopify admin view together.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by invoice number, Shopify reference, customer, or product..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {isSuperAdmin && paidSplitRequests.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={generateAllPendingInvoices}
                      disabled={bulkGeneratingInvoices}
                      className="gap-2"
                    >
                      {bulkGeneratingInvoices ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                      Generate All GST PDFs
                    </Button>
                  )}
                </div>

                {/* Paid Shopify Orders */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold">Paid Shopify Orders</h3>
                    <p className="text-sm text-muted-foreground">
                      All paid orders from the connected Shopify store, with SplitKart GST actions where applicable.
                    </p>
                  </div>
                  {paginatedPaidShopifyOrders.map((order) => {
                    const relatedRequest = order.legacyResourceId
                      ? splitRequestsByShopifyOrderId.get(String(order.legacyResourceId))
                      : undefined;
                    const relatedInvoice = order.legacyResourceId
                      ? splitInvoicesByShopifyOrderId.get(String(order.legacyResourceId))
                      : undefined;
                    const relatedProgress = relatedRequest ? getPaidCount(relatedRequest.participants) : null;
                    const relatedRequestReady = Boolean(
                      relatedRequest &&
                      relatedProgress &&
                      relatedProgress.total > 0 &&
                      relatedProgress.paid === relatedProgress.total &&
                      relatedRequest.status === "completed"
                    );

                    return (
                      <Card key={order.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-mono font-semibold text-primary">{order.orderName}</p>
                                <Badge className="bg-green-500/10 text-green-600">Paid</Badge>
                                <Badge variant="outline">{order.fulfillmentStatus}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {order.customerName}
                                {order.customerEmail ? ` • ${order.customerEmail}` : ""}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                <span>Created {new Date(order.createdAt).toLocaleDateString()}</span>
                                <span>Shopify Ref: {order.legacyResourceId ? `#${order.legacyResourceId}` : order.orderName}</span>
                                {relatedRequest?.product_name && <span>SplitKart Order: {relatedRequest.product_name}</span>}
                              </div>
                              {relatedRequest && relatedProgress && (
                                <p className="text-xs text-muted-foreground">
                                  SplitKart collection progress: {relatedProgress.paid}/{relatedProgress.total} paid
                                  {relatedRequest.shopify_order_id ? ` • Main Shopify Ref #${relatedRequest.shopify_order_id}` : ""}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <p className="font-bold">
                                {formatCurrency(order.totalAmount, (order.currencyCode as Currency) || storeCurrency)}
                              </p>
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {relatedRequest && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => openSplitOrderTracking(relatedRequest.id)}
                                      className="gap-1.5"
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" />
                                      Track Order
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => sendManualOrderEmail(relatedRequest)}
                                      disabled={sendingOrderEmailId === relatedRequest.id}
                                      className="gap-1.5"
                                    >
                                      {sendingOrderEmailId === relatedRequest.id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <Mail className="h-3.5 w-3.5" />
                                      )}
                                      Send Email
                                    </Button>
                                  </>
                                )}
                                {relatedInvoice ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => downloadExistingInvoiceFromPortal(relatedInvoice)}
                                    disabled={generatingInvoice === relatedInvoice.request_id}
                                    className="gap-1.5"
                                  >
                                    {generatingInvoice === relatedInvoice.request_id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <FileText className="h-3.5 w-3.5" />
                                    )}
                                    Download GST PDF
                                  </Button>
                                ) : relatedRequestReady && relatedRequest ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => generateInvoicesForRequest(relatedRequest)}
                                    disabled={generatingInvoice === relatedRequest.id}
                                    className="gap-1.5"
                                  >
                                    {generatingInvoice === relatedRequest.id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <FileText className="h-3.5 w-3.5" />
                                    )}
                                    Generate GST PDF
                                  </Button>
                                ) : (
                                  <Badge variant="outline" className="text-xs">
                                    {relatedRequest ? "Awaiting full SplitKart payment" : "Reference only"}
                                  </Badge>
                                )}
                                {order.adminOrderUrl && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(order.adminOrderUrl!, "_blank")}
                                    className="gap-1.5"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Open in Shopify
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {filteredPaidShopifyOrders.length === 0 && (
                    <div className="text-center py-12 border rounded-lg">
                      <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No paid Shopify orders found for this search</p>
                    </div>
                  )}
                  {renderSimplePagination(paidOrdersPage, totalPaidOrdersPages, setPaidOrdersPage)}
                </div>

                {/* Generated GST PDFs */}
                <div className="space-y-3">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold">Generated GST PDFs</h3>
                    <p className="text-sm text-muted-foreground">
                      Download any GST PDF that SplitKart has already generated and emailed from the portal itself.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {paginatedSplitInvoices.map((invoice) => (
                      <Card key={invoice.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${invoice.sent_at ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
                                <FileText className={`h-5 w-5 ${invoice.sent_at ? 'text-green-600' : 'text-yellow-600'}`} />
                              </div>
                              <div>
                                <p className="font-mono font-semibold text-primary">{invoice.invoice_number}</p>
                                <p className="text-sm text-muted-foreground">
                                  {invoice.participant_name || invoice.participant_email} • {invoice.product_title}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Generated {new Date(invoice.generated_at).toLocaleDateString()}
                                  {invoice.sent_at && ` • Sent ${new Date(invoice.sent_at).toLocaleDateString()}`}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Shopify Ref: {invoice.shopify_order_id ? `#${invoice.shopify_order_id}` : "Pending sync"}
                                  {invoice.shopify_order_name ? ` • ${invoice.shopify_order_name}` : ""}
                                </p>
                                <p className="text-xs text-primary font-medium">Final GST Invoice</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-bold">{formatCurrency(invoice.total_amount, (invoice.currency as Currency) || storeCurrency)}</p>
                                <Badge className={invoice.sent_at ? 'bg-green-500/10 text-green-600' : 'bg-yellow-500/10 text-yellow-600'}>
                                  {invoice.sent_at ? 'Sent' : 'Generated'}
                                </Badge>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadExistingInvoiceFromPortal(invoice)}
                                disabled={generatingInvoice === invoice.request_id}
                                className="gap-1.5"
                              >
                                {generatingInvoice === invoice.request_id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <ExternalLink className="h-3.5 w-3.5" />
                                )}
                                PDF
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {filteredSplitInvoices.length === 0 && (
                      <div className="text-center py-8">
                        <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No SplitKart GST invoices yet</p>
                      </div>
                    )}
                    {renderSimplePagination(generatedInvoicesPage, totalGeneratedInvoicesPages, setGeneratedInvoicesPage)}
                  </div>
                </div>
              </div>
            )}

            {/* Shipping & Tracking Sub-View */}
            {ordersSubView === "shipping" && (
              <div className="space-y-6">
                {/* Shipping Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Package className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Completed Orders</p>
                          <p className="text-2xl font-bold">{requests.filter(r => r.status === 'completed').length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                          <Truck className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Shipped</p>
                          <p className="text-2xl font-bold text-blue-600">{requests.filter(r => r.fulfillment_status === 'shipped' || r.fulfillment_status === 'out_for_delivery').length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Delivered</p>
                          <p className="text-2xl font-bold text-green-600">{requests.filter(r => r.fulfillment_status === 'delivered').length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-500/10 rounded-lg">
                          <Clock className="h-4 w-4 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Awaiting Shipment</p>
                          <p className="text-2xl font-bold text-yellow-600">{requests.filter(r => r.status === 'completed' && (r.fulfillment_status === 'pending' || !r.fulfillment_status)).length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Shipping List - only show completed/shipped/delivered orders */}
                <div className="space-y-3">
                  {requests
                    .filter(r => r.status === 'completed' || r.fulfillment_status === 'shipped' || r.fulfillment_status === 'out_for_delivery' || r.fulfillment_status === 'delivered')
                    .map((request) => {
                      const fulfillment = request.fulfillment_status || 'pending';
                      return (
                        <Card key={request.id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                {request.product_image_url && (
                                  <img src={request.product_image_url} alt={request.product_name} className="w-12 h-12 rounded-lg object-cover" />
                                )}
                                <div>
                                  <p className="font-semibold">{request.product_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {request.customer_name} • {formatCurrency(request.total_price, storeCurrency)}
                                  </p>
                                  {request.tracking_number && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <MapPin className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-xs font-mono text-muted-foreground">
                                        {request.carrier && `${request.carrier} • `}
                                        {request.tracking_number}
                                      </span>
                                      {request.tracking_url && (
                                        <Button
                                          size="sm"
                                          variant="link"
                                          className="h-auto p-0 text-xs"
                                          onClick={() => window.open(request.tracking_url!, '_blank')}
                                        >
                                          Track
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  {request.shipped_at && (
                                    <p className="text-xs text-muted-foreground">
                                      Shipped {new Date(request.shipped_at).toLocaleDateString()}
                                    </p>
                                  )}
                                  {request.delivered_at && (
                                    <p className="text-xs text-muted-foreground">
                                      Delivered {new Date(request.delivered_at).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                                <Badge className={
                                  fulfillment === 'delivered' ? 'bg-green-500/10 text-green-600' :
                                  fulfillment === 'shipped' || fulfillment === 'out_for_delivery' ? 'bg-blue-500/10 text-blue-600' :
                                  'bg-yellow-500/10 text-yellow-600'
                                }>
                                  {fulfillment === 'out_for_delivery' ? 'Out for Delivery' : 
                                   fulfillment === 'pending' ? 'Awaiting Shipment' : 
                                   fulfillment.charAt(0).toUpperCase() + fulfillment.slice(1)}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => window.open(`/split/${request.id}`, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  {requests.filter(r => r.status === 'completed' || r.fulfillment_status === 'shipped' || r.fulfillment_status === 'out_for_delivery' || r.fulfillment_status === 'delivered').length === 0 && (
                    <div className="text-center py-12">
                      <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No orders ready for shipping yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Orders will appear here once all participants have paid</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Refunds Sub-View */}
            {ordersSubView === "refunds" && merchantInfo && (
              <RefundManagement 
                merchantId={merchantInfo.id}
                currency={merchantInfo.currency || 'INR'}
              />
            )}
            </div>
          )}

            {/* Products Tab removed - split payments are now store-wide */}

            {activeTab === "wallet" && (
              <div className="space-y-6">
                {merchantInfo && usesWalletBilling ? (
                  <MerchantWalletPanel
                    billingProvider={merchantInfo.billing_provider || "razorpay"}
                    merchantId={merchantInfo.id}
                    platformType={merchantInfo.platform_type}
                    signupSource={merchantInfo.signup_source}
                  />
                ) : (
                  <Card>
                    <CardContent className="py-10 text-center">
                      <Wallet className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
                      <p className="font-medium">Wallet not required for this store</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Shopify App Store merchants use Shopify billing directly. Wallet-based commission is available for WooCommerce, direct Shopify, and Razorpay-billed merchants.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {activeTab === "settings" && (
              <div className="space-y-6">
                {settingsSubView === "connector" && canAccessSettings && merchantInfo && (
                  <MerchantConnectorPanel
                    merchantId={merchantInfo.id}
                    platformType={merchantInfo.platform_type}
                    shopifyAccessToken={merchantInfo.shopify_access_token}
                    storeDomain={merchantInfo.shopify_store_domain}
                    webhookSecret={merchantInfo.webhook_secret}
                    widgetInstalled={merchantInfo.widget_installed}
                    onOpenPreferences={() => setSettingsSubView("preferences")}
                  />
                )}

                {/* Billing Sub-View - super_admin only */}
                {settingsSubView === "billing" && canAccessSettings && (
                  <div className="space-y-6">
                    {/* Subscription & Billing Section */}
                    {/* Current Plan Summary */}
                    {billingSubscription && (
                      <Card className="border-primary/20 bg-primary/5">
                        <CardContent className="pt-6 pb-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">Current Plan</p>
                              <p className="text-2xl font-bold">{billingPlanLabel}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {billingSubscription.status === 'trialing' 
                                  ? `30-day intro trial • ${billingSubscription.daysRemaining ?? 0} days remaining`
                                  : billingSubscription.status === 'active'
                                  ? 'Active subscription'
                                  : 'Approval required'}
                              </p>
                            </div>
                            <Badge variant={billingSubscription.status === 'active' ? 'default' : 'secondary'} className="capitalize">
                              {billingSubscription.status === 'trialing' ? 'Trial' : billingSubscription.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Upgrade / Plan Selection */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <CreditCard className="h-5 w-5" />
                          {billingSubscription?.plan === 'trial' || billingSubscription?.status === 'canceled' || billingSubscription?.status === 'past_due' || billingSubscription?.status === 'paused'
                            ? 'Choose a Plan'
                            : 'Change Plan'}
                        </CardTitle>
                        <CardDescription>
                          {billingSubscription?.status === 'canceled' || billingSubscription?.status === 'past_due' || billingSubscription?.status === 'paused'
                            ? usesWalletBilling
                              ? 'Pay through Razorpay to reactivate billing for this store.'
                              : 'Approve a Shopify-billed plan to reactivate billing for this store.'
                            : billingSubscription?.status === 'trialing' 
                            ? 'New merchants get 30 days, 100 free splits, and 0% commission before paid billing starts.'
                            : 'Upgrade or change your subscription plan.'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {merchantInfo && billingSubscription && (
                          <SubscriptionPayment
                            billingProvider={merchantInfo.billing_provider}
                            currentPlan={billingSubscription.plan}
                            currentStatus={billingSubscription.status}
                            platformType={merchantInfo.platform_type}
                            signupSource={merchantInfo.signup_source}
                            onSuccess={async () => {
                              if (merchantInfo) {
                                await fetchRequests(merchantInfo.id);
                                window.location.reload();
                              }
                            }}
                          />
                        )}
                      </CardContent>
                    </Card>

                {/* Payment History */}
                {merchantInfo && (
                  <PaymentHistory merchantId={merchantInfo.id} />
                )}

                {/* Fee Stats moved to Preferences → Payments & Cancellation */}

                {/* Shakatah Invoices */}
                {shakatahInvoices.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Receipt className="h-5 w-5" />
                        Shakatah Invoices
                      </CardTitle>
                      <CardDescription>Your billing history with Shakatah</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {shakatahInvoices.map((invoice) => (
                          <div key={invoice.id} className="flex items-center justify-between p-4 rounded-lg border">
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${
                                invoice.status === 'paid' ? 'bg-green-500/10' : 'bg-blue-500/10'
                              }`}>
                                <Receipt className={`h-5 w-5 ${
                                  invoice.status === 'paid' ? 'text-green-600' : 'text-blue-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-mono font-semibold">{invoice.invoice_number}</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-semibold">{formatCurrency(invoice.total_amount, storeCurrency)}</p>
                                <p className="text-xs text-muted-foreground">{invoice.total_fees} fee(s)</p>
                              </div>
                              <Badge className={
                                invoice.status === 'paid' ? 'bg-green-500/10 text-green-600' : 
                                invoice.status === 'sent' ? 'bg-blue-500/10 text-blue-600' :
                                'bg-yellow-500/10 text-yellow-600'
                              }>
                                {invoice.status === 'paid' ? 'Paid' : 
                                 invoice.status === 'sent' ? 'Sent' : 'Draft'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Fee Details moved to Preferences → Payments & Cancellation */}

                {/* Financial Statement Download */}
                {merchantInfo && (
                  <FinancialStatement 
                    merchantId={merchantInfo.id}
                    merchantInfo={{
                      store_name: merchantInfo.store_name,
                      shopify_store_domain: merchantInfo.shopify_store_domain,
                      business_address: merchantInfo.business_address,
                      gst_number: merchantInfo.gst_number,
                      business_email: merchantInfo.business_email,
                      business_phone: merchantInfo.business_phone,
                    }}
                    currency={storeCurrency}
                  />
                )}
              </div>
            )}

            {/* Preferences Sub-View - super_admin only */}
            {settingsSubView === "preferences" && canAccessSettings && merchantInfo && (
              <MerchantPreferences
                merchantId={merchantInfo.id}
                storeDomain={merchantInfo.shopify_store_domain}
                canEdit={canAccessSettings}
                currentPlan={subscription?.plan}
                onNavigateToBilling={() => setSettingsSubView("billing")}
                merchantEmail={merchantInfo.notification_email}
                isSuperAdmin={isSuperAdmin}
                platformType={merchantInfo.platform_type}
              />
            )}

            {settingsSubView === "shipping" && canAccessSettings && merchantInfo?.platform_type === "shopify" && (
              <ShopifyShippingDetails
                merchantId={merchantInfo.id}
                storeDomain={merchantInfo.shopify_store_domain}
              />
            )}

            {/* Notifications Sub-View */}
            {settingsSubView === "notifications" && merchantInfo && (
              <NotificationSettings
                merchantId={merchantInfo.id}
                canEdit={canAccessSettings || !isViewOnly}
                merchantEmail={merchantInfo.notification_email || user?.email}
              />
            )}

            {/* Store Info Sub-View - super_admin only */}
            {settingsSubView === "info" && canAccessSettings && merchantInfo && (
              <MerchantSettings
                merchantId={merchantInfo.id}
                storeDomain={merchantInfo.shopify_store_domain}
                storeName={merchantInfo.store_name}
                canEdit={canAccessSettings}
                isSuperAdmin={isSuperAdmin}
                currentPlan={merchantInfo.plan}
                onNavigateToBilling={() => setSettingsSubView("billing")}
                
              />
            )}
              </div>
            )}
          </div>
        </main>
      </div>
      
      <FAQChatBot context="merchant" />
      
      {/* Exit confirmation dialog */}
      <ExitConfirmationDialog
        open={showExitDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
      
      {/* Footer for Dashboard */}
      <CustomerFooter tagline="Powering split payments for merchants" variant="merchant" />
    </div>
  );
};

export default MerchantDashboard;
